import { Component, inject, OnInit, signal, HostListener, LOCALE_ID, computed, DestroyRef } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { PharmacyListItem, PharmacyFilters } from '../../../../services/pharmacy/pharmacy.model';
import { LookupService } from '../../../../services/pharmacy/lookup.service';
import { RequestService } from '../../../../services/pharmacy/request.service';
import { AssignEmployeeDialog } from '../assign-employee-dialog/assign-employee-dialog';
import { PharmacyReasonDialog } from '../dialogs/pharmacy-reason-dialog/pharmacy-reason-dialog';
import { ApprovePharmacyDialog } from '../dialogs/approve-pharmacy-dialog/approve-pharmacy-dialog';
import { Filter } from './filter/filter';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { filter, finalize } from 'rxjs';
import { Loading } from '../../../../utils/loading/loading';
import { LoadState } from './admin-pharmacies-list.model';
import { ActionItem, PharmacyRequestStatus } from '../../../../pages/admin-dashboard/pharmacies/pharmacies.model';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { ActionInterface } from '../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { toPharmacyTableRowsList } from './admin-pharmacies-list.mapper';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';

@Component({
  selector: 'app-admin-pharmacies-list',
  imports: [
    FormsModule,
    DatePipe,
    AssignEmployeeDialog,
    PharmacyReasonDialog,
    ApprovePharmacyDialog,
    RouterLink,
    Filter,
    Loading,
    SuccessfulNotification,
    HasScopeDirective,
    Tables,
    ErrorData,
    Pagination,
  ],
  templateUrl: './admin-pharmacies-list.html',
  styleUrl: './admin-pharmacies-list.scss',
})
export class AdminPharmaciesList implements OnInit {
  readonly SCOPES = SCOPES;
  private pharmacyService = inject(PharmacyService);
  private lookupService = inject(LookupService);
  private requestService = inject(RequestService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  modalType = signal<'assign' | 're-assign'>('assign');
  state = signal<LoadState>('loading');

  pharmacies = signal<PharmacyListItem[] | null>(null);
  statuses = signal<LookupVTO[]>([]);
  activeDropdownId = signal<number | null>(null);
  currentActions = signal<ActionInterface[]>([]);
  loadingActions = signal<boolean>(false);

  assigningEmployeePharmacy = signal<PharmacyListItem | null>(null);

  assignEmployeeError = signal<string | null>(null);
  rejectingPharmacyId = signal<number | null>(null);
  modifyingPharmacyId = signal<number | null>(null);
  approvingPharmacyId = signal<number | null>(null);
  approvingPharmacyName = signal<string | null>(null);
  deletingPharmacyId = signal<number | null>(null);
  successRequestModification = signal<boolean>(false);
  successAssignEmployee = signal<boolean>(false);

  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(25);
  totalItems = 0;

  headerItems = signal([
    { key: 'nickName', label: $localize`:@@adminPharmaciesTableNickname:Pharmacy Nickname` },
    { key: 'pharmacyOwner', label: $localize`:@@adminPharmaciesTableOwner:Pharmacy Owner` },
    { key: 'status', label: $localize`:@@adminPharmaciesFilterStatus:Status`, type: 'status' },
    {
      key: 'assignedTo',
      label: $localize`:@@adminpharmaciesListAssignedTo:Assigned To`,
      scope: this.SCOPES.PHARMACIES.AUTO_COMPLETE_BY_EMPLOYEE_NAME,
    },
    { key: 'createdOnBy', label: $localize`:@@adminListPharmaciesCreatedOnFrom:Created On / By` },
    { key: 'lastModifiedOnBy', label: $localize`:@@adminListPharmaciesCreatedOnFromLastUpdated:Last Updated On / By` },
    { key: 'actions', label: '', type: 'actions' },
  ]);
  rows = computed(() => toPharmacyTableRowsList(this.pharmacies()!, this.isArabic()));

  filters = signal<PharmacyFilters>({
    pharmacyOwnerId: undefined,
    assignedToId: undefined,
    statusId: undefined,
    createdOnFrom: undefined,
    createdOnTo: undefined,
    lastModifiedOnFrom: undefined,
    lastModifiedOnTo: undefined,
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
  });

  scopesForAction = signal<number | number[] | null>(null);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.activeDropdownId.set(null);
  }

  ngOnInit() {
    this.loadStatuses();
    this.loadPharmacies();
  }

  toggleDropdown(id?: any, event?: Event) {
    event?.stopPropagation();
    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      const pharmacyStatusId = this.pharmacies()?.filter((pharmacy) => pharmacy.id === id)[0].status.id;
      this.getActions(pharmacyStatusId!);
    }
  }

  getActionsForRow = (row: Record<string, any>): ActionInterface[] => {
    const statusId = row['statusId'];
    this.getActions(statusId);
    return this.currentActions();
  };

  onTableAction(event: { actionId: number; row: Record<string, any> }) {
    const pharmacyId = event.row['id'];
    const entity = this.pharmacies()?.find((p) => p.id === pharmacyId);

    if (entity) {
      this.handleAction({ event: new MouseEvent('click'), actionId: event.actionId, entity });
    } else {
      console.error('Pharmacy not found for ID:', pharmacyId);
    }
  }

  VIEW_PHARMACY_DETAILS = signal<LookupVTO>({
    id: 6,
    titleEn: 'View Pharmacy Details',
    titleAr: 'عرض تفاصيل الصيدلية',
  });

  getActions(statusId: number) {
    switch (statusId) {
      case PharmacyRequestStatus.Pending:
        this.currentActions.set([
          { id: 1, titleEn: 'Assign Employee', titleAr: 'تعيين موظف', scope: 'ASSIGN_EMPLOYEE' },
          this.VIEW_PHARMACY_DETAILS(),
        ]);
        return;
      case PharmacyRequestStatus.InProgress:
        this.currentActions.set([
          { titleEn: 'Accept Pharmacy Request', titleAr: 'قبول طلب الصيدلية', id: 2 },
          { titleEn: 'Reject Pharmacy Request', titleAr: 'رفض طلب الصيدلية', id: 3, scope: 'REJECT' },
          { titleEn: 'Request Modification', titleAr: 'طلب تعديل', id: 4 },
          { titleEn: 'Reassign Employee', titleAr: 'إعادة تعيين موظف', id: 5 },
          this.VIEW_PHARMACY_DETAILS(),
        ]);
        return;

      case PharmacyRequestStatus.Feedback:
        this.currentActions.set([
          { titleEn: 'Request Modification', titleAr: 'طلب تعديل', id: 4 },
          this.VIEW_PHARMACY_DETAILS(),
        ]);
        return;

      case PharmacyRequestStatus.Accepted:
      case PharmacyRequestStatus.Expired:
      case PharmacyRequestStatus.Rejected:
      case PharmacyRequestStatus.Cancelled:
        this.currentActions.set([this.VIEW_PHARMACY_DETAILS()]);
        return;

      default:
        return [];
    }
  }

  loadStatuses() {
    this.pharmacyService.getAllStatuses().subscribe((res) => {
      this.statuses.set(res.list);
    });
  }

  loadPharmacies() {
    this.filters.set({
      ...this.filters(),
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
    });

    this.state.set('loading');

    const subscription = this.pharmacyService.getAllPharmacies(this.filters()).subscribe({
      next: (res) => {
        this.pharmacies.set(res.list);
        this.totalItems = res.total;
        this.state.set('success');
      },
      error: (error) => {
        this.state.set('error');
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  hasActiveFilters(): boolean {
    return (
      this.filters().pharmacyOwnerId !== undefined ||
      this.filters().assignedToId !== undefined ||
      this.filters().statusId !== undefined ||
      this.filters().createdOnFrom !== undefined ||
      this.filters().createdOnTo !== undefined ||
      this.filters().lastModifiedOnFrom !== undefined ||
      this.filters().lastModifiedOnTo !== undefined
    );
  }

  toggleSortOrder() {
    this.filters.set({
      ...this.filters(),
      orderDir: this.filters().orderDir === 'ASC' ? 'DESC' : 'ASC',
    });

    this.loadPharmacies();
  }

  applyFilters(filterApplied: {
    createdOnFrom?: string | undefined;
    createdOnTo?: string | undefined;
    lastModifiedOnFrom?: string | undefined;
    lastModifiedOnTo?: string | undefined;
    statusIds?: number[];
  }) {
    this.currentPage.set(1);
    this.filters.set({
      ...this.filters(),
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
      createdOnFrom: filterApplied.createdOnFrom,
      createdOnTo: filterApplied.createdOnTo,
      lastModifiedOnFrom: filterApplied.lastModifiedOnFrom,
      lastModifiedOnTo: filterApplied.lastModifiedOnTo,
      statusId: filterApplied.statusIds?.join(',').toString(),
    });

    this.loadPharmacies();
  }

  resetFilters() {
    this.filters.set({
      pharmacyOwnerId: undefined,
      assignedToId: undefined,
      statusId: undefined,
      createdOnFrom: undefined,
      createdOnTo: undefined,
      lastModifiedOnFrom: undefined,
      lastModifiedOnTo: undefined,
      orderBy: 'LAST_MODIFICATION_DATE',
      orderDir: 'DESC',
    });
    this.currentPage.set(1);
    this.loadPharmacies();
  }

  selectePharmacyOwner(item: { id: number; fullName: string }) {
    this.updateFilter('pharmacyOwnerId', item.id);
  }
  selectEmployee(item: { id: number; fullName: string }) {
    this.updateFilter('assignedToId', item.id);
  }

  updateFilter(key: keyof PharmacyFilters, value: any) {
    this.filters.update((prev) => ({ ...prev, [key]: value }));
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage());
  }

  get startItem(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  }

  get endItem(): number {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems ? this.totalItems : end;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      this.loadPharmacies();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.set(this.currentPage() + 1);
      this.loadPharmacies();
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.set(this.currentPage() - 1);
      this.loadPharmacies();
    }
  }

  getPageNumbers(): number[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage();
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    range.push(1);

    if (totalPages <= 1) {
      return range;
    }

    for (let i = this.currentPage() - delta; i <= this.currentPage() + delta; i++) {
      if (i < totalPages && i > 1) {
        range.push(i);
      }
    }
    range.push(totalPages);

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push(-1);
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  }

  onPageSizeChange() {
    this.currentPage.set(1);

    this.updateFilter('pageNum', 0);
    this.updateFilter('pageSize', Number(this.itemsPerPage()));
    this.loadPharmacies();
  }

  getStatusClass(status: string): string {
    if (!status) return 'bg-[#F3F4F6] text-[#6B7280]';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('active') || statusLower.includes('accepted')) {
      return 'bg-[#D1FAE5] text-[#059669]';
    } else if (statusLower.includes('pending')) {
      return 'bg-[#FEF3C7] text-[#D97706]';
    } else if (statusLower.includes('progress')) {
      return 'bg-[#DBEAFE] text-[#2563EB]';
    } else if (statusLower.includes('reject') || statusLower.includes('rrject') || statusLower.includes('delet')) {
      return 'bg-[#FEE2E2] text-[#DC2626]';
    } else if (statusLower.includes('cancel')) {
      return 'bg-[#FFEDD5] text-[#EA580C]';
    } else {
      return 'bg-[#F3F4F6] text-[#6B7280]';
    }
  }

  handleAction(e: { event: Event; actionId: number; entity: PharmacyListItem }) {
    const { event, actionId, entity } = e;
    event?.stopPropagation();

    if (actionId === 6) {
      this.router.navigate(['admin', 'pharmacies', 'view', entity.id]);
    } else if (actionId === 5 || actionId === 1) {
      if (actionId === 1) this.modalType.set('assign');
      if (actionId === 5) this.modalType.set('re-assign');
      this.assigningEmployeePharmacy.set(entity);
      this.assignEmployeeError.set(null);
    } else if (actionId === 3) {
      this.rejectingPharmacyId.set(entity.id);
    } else if (actionId === 4) {
      this.modifyingPharmacyId.set(entity.id);
    } else if (actionId === 2) {
      this.approvingPharmacyId.set(entity.id);
      this.approvingPharmacyName.set(entity.nickName);
    }
  }

  closeAssignEmployeeDialog() {
    this.assigningEmployeePharmacy.set(null);
    this.assignEmployeeError.set(null);
  }

  onEmployeeAssigned(item: { action: LookupVTO }) {
    if (this.assigningEmployeePharmacy()) {
      const pharmacy = this.assigningEmployeePharmacy()!;
      this.pharmacies.update(
        (list) => list?.map((p) => (p.id === pharmacy.id ? { ...p, status: item.action } : p)) ?? [],
      );
      this.successAssignEmployee.set(true);
      this.toggleDropdown(this.assigningEmployeePharmacy()?.id!, new MouseEvent('click'));
    }
  }

  closeRejectDialog() {
    this.rejectingPharmacyId.set(null);
  }

  onReject(item: { action: LookupVTO }) {
    const pharmacyId = this.rejectingPharmacyId()!;
    this.pharmacies.update((list) => list?.map((p) => (p.id === pharmacyId ? { ...p, status: item.action } : p)) ?? []);
  }

  closeModifyDialog() {
    this.modifyingPharmacyId.set(null);
  }

  onModify(item: { action: LookupVTO }) {
    const pharmacyId = this.modifyingPharmacyId()!;
    this.pharmacies.update((list) => list?.map((p) => (p.id === pharmacyId ? { ...p, status: item.action } : p)) ?? []);
    this.successRequestModification.set(true);
  }

  closeApproveDialog() {
    this.approvingPharmacyId.set(null);
    this.approvingPharmacyName.set(null);
  }

  onApproveSuccess() {
    this.closeApproveDialog();
    this.loadPharmacies();
  }

  closeDeleteDialog() {
    this.deletingPharmacyId.set(null);
  }

  isArabic = computed(() => this.locale.startsWith('ar'));
  isEnglish = computed(() => this.locale.startsWith('en'));

  navigateToRequestDetails(id: number) {
    this.router.navigate(['/admin/pharmacies/view/', id]);
  }

  getScopeValue(scope: string | undefined): number[] {
    if (!scope) return [];
    return [SCOPES.PHARMACIES[scope as keyof typeof SCOPES.PHARMACIES]];
  }

  changePageNumber(itemNum: number) {
    this.itemsPerPage.set(itemNum);
  }
}
